import { Route, Navigate, useParams } from 'react-router-dom';
import DashboardPage from '../pages/portal/DashboardPage';
import EmployeesPage from '../pages/portal/EmployeesPage';
import StaffIntroductionsPage from '../pages/portal/StaffIntroductionsPage';
import EmployeeProfilePage from '../pages/portal/EmployeeProfilePage';
import EmployeeStaffCommunicationsPage from '../pages/portal/EmployeeStaffCommunicationsPage';
import StaffCommunicationsHubPage from '../pages/portal/StaffCommunicationsHubPage';
import RecruitmentPipelinePage from '../pages/portal/RecruitmentPage';
import TrainingPage from '../pages/portal/TrainingPage';
import AuditViewPage from '../pages/portal/AuditViewPage';
import DBSRegisterPage from '../pages/portal/DBSRegisterPage';
import SettingsPage from '../pages/portal/SettingsPage';
import SettingsIntegrationsPage from '../pages/portal/SettingsIntegrationsPage';
import TemplatesPage from '../pages/portal/TemplatesPage';
import DocumentTemplateLibraryPage from '../pages/portal/DocumentTemplateLibraryPage';
import FormEditorPage from '../pages/portal/FormEditorPage';
import ComplianceCentrePage from '../pages/portal/ComplianceCentrePage';
import ComplianceAlertsPage from '../pages/portal/ComplianceAlertsPage';
import SupportPage from '../pages/portal/SupportPage';
import ServiceUsersPage from '../pages/portal/ServiceUsersPage';
import ServiceUserProfilePage from '../pages/portal/ServiceUserProfilePage';
import AdminUsersPage from '../pages/portal/AdminUsersPage';
import ServiceUserFeedbackPage from '../pages/portal/ServiceUserFeedbackPage';
import ComplaintsHandlingPage from '../pages/portal/ComplaintsHandlingPage';
import ShiftsPage from '../pages/portal/ShiftsPage';
import TimesheetsPage from '../pages/portal/TimesheetsPage';
import PayrollPage from '../pages/portal/PayrollPage';
import InvoicesPage from '../pages/portal/InvoicesPage';
import AccountsSummaryPage from '../pages/portal/AccountsSummaryPage';
import CareLocationsPage from '../pages/portal/CareLocationsPage';
import OrganisationsPage from '../pages/portal/OrganisationsPage';
import ClientDetailPage from '../pages/portal/ClientDetailPage';
import StaffingRequestsPage from '../pages/portal/StaffingRequestsPage';
import CareEnquiriesPage from '../pages/portal/CareEnquiriesPage';
import ContactSubmissionsPage from '../pages/portal/ContactSubmissionsPage';
import SalesLeadsPage from '../pages/portal/SalesLeadsPage';
import PortalInboxEntryPage from '../pages/portal/PortalInboxEntryPage';
import LeadDiscoveryPage from '../pages/portal/LeadDiscoveryPage';
import CareOpsOwnerPage from '../pages/portal/CareOpsOwnerPage';
import PlansBillingPage from '../pages/portal/PlansBillingPage';
import PrivacyRequestsPage from '../pages/portal/PrivacyRequestsPage';

/** Remount the profile for each person so previous applicant evidence cannot flash on screen. */
function KeyedEmployeeProfilePage() {
  const { employeeId } = useParams();
  return <EmployeeProfilePage key={employeeId} />;
}

/**
 * Relative portal routes nested under /portal in App.js.
 * Must be direct <Route> children — not wrapped in a custom component (RR v7).
 */
export const portalNestedRouteElements = [
  <Route key="index" index element={<DashboardPage />} />,
  <Route key="dashboard" path="dashboard" element={<DashboardPage />} />,
  <Route key="staff-communications-hub" path="staff-communications" element={<StaffCommunicationsHubPage />} />,
  <Route key="legacy-people-communications" path="people/communications" element={<StaffCommunicationsHubPage />} />,
  <Route key="employees" path="employees" element={<EmployeesPage />} />,
  <Route key="staff-introductions" path="staff-introductions" element={<StaffIntroductionsPage />} />,
  <Route key="employee-staff-communications" path="employees/:employeeId/communications" element={<EmployeeStaffCommunicationsPage />} />,
  <Route key="employee-profile" path="employees/:employeeId" element={<KeyedEmployeeProfilePage />} />,
  <Route key="recruitment" path="recruitment" element={<RecruitmentPipelinePage />} />,
  <Route key="recruitment-profile" path="recruitment/:employeeId" element={<KeyedEmployeeProfilePage />} />,
  <Route key="bulk-import" path="bulk-import" element={<Navigate to="/portal/recruitment" replace />} />,
  <Route key="training" path="training" element={<TrainingPage />} />,
  <Route key="dbs-register" path="dbs-register" element={<DBSRegisterPage />} />,
  <Route key="templates" path="templates" element={<TemplatesPage />} />,
  <Route key="template-library" path="template-library" element={<DocumentTemplateLibraryPage />} />,
  <Route key="compliance-centre" path="compliance-centre" element={<ComplianceCentrePage />} />,
  <Route key="support" path="support" element={<SupportPage />} />,
  <Route key="applications" path="applications" element={<Navigate to="/portal/recruitment" replace />} />,
  <Route key="cqc-dashboard" path="cqc-dashboard" element={<Navigate to="/portal/compliance-centre?tab=reports" replace />} />,
  <Route key="feedback" path="feedback" element={<ServiceUserFeedbackPage />} />,
  <Route key="complaints" path="complaints" element={<ComplaintsHandlingPage />} />,
  <Route key="incidents" path="incidents" element={<Navigate to="/portal/compliance-centre?tab=incidents" replace />} />,
  <Route key="compliance-alerts" path="compliance-alerts" element={<ComplianceAlertsPage />} />,
  <Route key="privacy-requests" path="privacy-requests" element={<PrivacyRequestsPage />} />,
  <Route key="forms" path="forms/:formId" element={<FormEditorPage />} />,
  <Route key="audit" path="audit" element={<AuditViewPage />} />,
  <Route key="global-audit" path="global-audit" element={<Navigate to="/portal/audit" replace />} />,
  <Route key="service-users" path="service-users" element={<ServiceUsersPage />} />,
  <Route key="service-user-profile" path="service-users/:id" element={<ServiceUserProfilePage />} />,
  <Route key="shifts" path="shifts" element={<ShiftsPage />} />,
  <Route key="timesheets" path="timesheets" element={<TimesheetsPage />} />,
  <Route key="payroll" path="payroll" element={<PayrollPage />} />,
  <Route key="invoices" path="invoices" element={<InvoicesPage />} />,
  <Route key="accounts" path="accounts" element={<AccountsSummaryPage />} />,
  <Route key="care-locations" path="care-locations" element={<CareLocationsPage />} />,
  <Route key="scheduled-requests" path="scheduled-requests" element={<Navigate to="/portal/compliance-centre" replace />} />,
  <Route key="lead-discovery" path="lead-discovery" element={<LeadDiscoveryPage />} />,
  <Route key="sales-leads" path="sales-leads" element={<SalesLeadsPage />} />,
  <Route key="inbox" path="inbox" element={<PortalInboxEntryPage />} />,
  <Route key="sales-inbox" path="sales-inbox" element={<Navigate to="/portal/inbox?department=staffing" replace />} />,
  <Route key="organisations" path="organisations" element={<OrganisationsPage />} />,
  <Route key="organisation-detail" path="organisations/:orgId" element={<ClientDetailPage />} />,
  <Route key="staffing-requests" path="staffing-requests" element={<StaffingRequestsPage />} />,
  <Route key="care-enquiries" path="care-enquiries" element={<CareEnquiriesPage />} />,
  <Route key="contact-submissions" path="contact-submissions" element={<ContactSubmissionsPage />} />,
  <Route key="admin-users" path="admin-users" element={<AdminUsersPage />} />,
  <Route key="plans-billing" path="plans-billing" element={<PlansBillingPage />} />,
  <Route key="careops-admin" path="careops-admin" element={<CareOpsOwnerPage />} />,
  <Route key="settings-integrations" path="settings/integrations" element={<SettingsIntegrationsPage />} />,
  <Route key="settings" path="settings" element={<SettingsPage />} />,
];

/**
 * Absolute portal routes for workspace MemoryRouter windows.
 */
export const portalAbsoluteRouteElements = [
  <Route key="portal-root" path="/portal" element={<Navigate to="/portal/dashboard" replace />} />,
  <Route key="dashboard" path="/portal/dashboard" element={<DashboardPage />} />,
  <Route key="staff-communications-hub" path="/portal/staff-communications" element={<StaffCommunicationsHubPage />} />,
  <Route key="legacy-people-communications" path="/portal/people/communications" element={<StaffCommunicationsHubPage />} />,
  <Route key="employees" path="/portal/employees" element={<EmployeesPage />} />,
  <Route key="employee-staff-communications" path="/portal/employees/:employeeId/communications" element={<EmployeeStaffCommunicationsPage />} />,
  <Route key="employee-profile" path="/portal/employees/:employeeId" element={<KeyedEmployeeProfilePage />} />,
  <Route key="recruitment" path="/portal/recruitment" element={<RecruitmentPipelinePage />} />,
  <Route key="recruitment-profile" path="/portal/recruitment/:employeeId" element={<KeyedEmployeeProfilePage />} />,
  <Route key="bulk-import" path="/portal/bulk-import" element={<Navigate to="/portal/recruitment" replace />} />,
  <Route key="training" path="/portal/training" element={<TrainingPage />} />,
  <Route key="dbs-register" path="/portal/dbs-register" element={<DBSRegisterPage />} />,
  <Route key="templates" path="/portal/templates" element={<TemplatesPage />} />,
  <Route key="template-library" path="/portal/template-library" element={<DocumentTemplateLibraryPage />} />,
  <Route key="compliance-centre" path="/portal/compliance-centre" element={<ComplianceCentrePage />} />,
  <Route key="support" path="/portal/support" element={<SupportPage />} />,
  <Route key="applications" path="/portal/applications" element={<Navigate to="/portal/recruitment" replace />} />,
  <Route key="cqc-dashboard" path="/portal/cqc-dashboard" element={<Navigate to="/portal/compliance-centre?tab=reports" replace />} />,
  <Route key="feedback" path="/portal/feedback" element={<ServiceUserFeedbackPage />} />,
  <Route key="complaints" path="/portal/complaints" element={<ComplaintsHandlingPage />} />,
  <Route key="incidents" path="/portal/incidents" element={<Navigate to="/portal/compliance-centre?tab=incidents" replace />} />,
  <Route key="compliance-alerts" path="/portal/compliance-alerts" element={<ComplianceAlertsPage />} />,
  <Route key="privacy-requests" path="/portal/privacy-requests" element={<PrivacyRequestsPage />} />,
  <Route key="forms" path="/portal/forms/:formId" element={<FormEditorPage />} />,
  <Route key="audit" path="/portal/audit" element={<AuditViewPage />} />,
  <Route key="global-audit" path="/portal/global-audit" element={<Navigate to="/portal/audit" replace />} />,
  <Route key="service-users" path="/portal/service-users" element={<ServiceUsersPage />} />,
  <Route key="service-user-profile" path="/portal/service-users/:id" element={<ServiceUserProfilePage />} />,
  <Route key="shifts" path="/portal/shifts" element={<ShiftsPage />} />,
  <Route key="timesheets" path="/portal/timesheets" element={<TimesheetsPage />} />,
  <Route key="payroll" path="/portal/payroll" element={<PayrollPage />} />,
  <Route key="invoices" path="/portal/invoices" element={<InvoicesPage />} />,
  <Route key="accounts" path="/portal/accounts" element={<AccountsSummaryPage />} />,
  <Route key="care-locations" path="/portal/care-locations" element={<CareLocationsPage />} />,
  <Route key="scheduled-requests" path="/portal/scheduled-requests" element={<Navigate to="/portal/compliance-centre" replace />} />,
  <Route key="lead-discovery" path="/portal/lead-discovery" element={<LeadDiscoveryPage />} />,
  <Route key="sales-leads" path="/portal/sales-leads" element={<SalesLeadsPage />} />,
  <Route key="inbox" path="/portal/inbox" element={<PortalInboxEntryPage />} />,
  <Route key="inbox-splat" path="/portal/inbox/*" element={<PortalInboxEntryPage />} />,
  <Route key="sales-inbox" path="/portal/sales-inbox" element={<Navigate to="/portal/inbox?department=staffing" replace />} />,
  <Route key="organisations" path="/portal/organisations" element={<OrganisationsPage />} />,
  <Route key="organisation-detail" path="/portal/organisations/:orgId" element={<ClientDetailPage />} />,
  <Route key="staffing-requests" path="/portal/staffing-requests" element={<StaffingRequestsPage />} />,
  <Route key="care-enquiries" path="/portal/care-enquiries" element={<CareEnquiriesPage />} />,
  <Route key="contact-submissions" path="/portal/contact-submissions" element={<ContactSubmissionsPage />} />,
  <Route key="admin-users" path="/portal/admin-users" element={<AdminUsersPage />} />,
  <Route key="plans-billing" path="/portal/plans-billing" element={<PlansBillingPage />} />,
  <Route key="careops-admin" path="/portal/careops-admin" element={<CareOpsOwnerPage />} />,
  <Route key="settings-integrations" path="/portal/settings/integrations" element={<SettingsIntegrationsPage />} />,
  <Route key="settings" path="/portal/settings" element={<SettingsPage />} />,
];
